import { useState, useEffect } from 'react';
import { 
  getNotificationPermission, 
  requestNotificationPermission, 
  isNotificationSupported 
} from '../../utils/osNotifications';

/**
 * Banner that prompts user to enable OS notifications for chat messages.
 * Shows only if:
 * - Browser supports notifications
 * - Permission is 'default' (not yet asked)
 * - User hasn't dismissed the banner
 */
export function NotificationPermissionBanner() {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem('notificationBannerDismissed') === 'true';
  });
  const [isRequesting, setIsRequesting] = useState(false);

  // Update permission state when it changes
  useEffect(() => {
    const interval = setInterval(() => {
      setPermission(getNotificationPermission());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleEnable = async () => {
    setIsRequesting(true);
    const result = await requestNotificationPermission();
    setPermission(result);
    setIsRequesting(false);
    
    if (result === 'granted') {
      setIsDismissed(true);
      localStorage.setItem('notificationBannerDismissed', 'true');
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('notificationBannerDismissed', 'true');
  };

  // Don't show if:
  // - Not supported
  // - Already granted or denied
  // - User dismissed
  if (!isNotificationSupported() || permission !== 'default' || isDismissed) {
    return null;
  }

  return (
    <div className="bg-primary/10 border-l-4 border-primary px-4 py-3 mb-4 rounded-r-lg">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-text mb-1">
            Enable Desktop Notifications
          </h3>
          <p className="text-xs text-muted leading-relaxed">
            Get notified when you receive new chat messages, even when BandMate is in the background. 
            Privacy mode available to hide message content.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-3 py-1.5 text-xs text-muted hover:text-text transition-colors rounded-lg"
          >
            Not now
          </button>
          <button
            onClick={handleEnable}
            disabled={isRequesting}
            className="px-3 py-1.5 text-xs text-white bg-primary hover:bg-primary/90 disabled:opacity-50 transition-colors rounded-lg font-medium"
          >
            {isRequesting ? 'Requesting...' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}
